import React from 'react';
import { Alert, Box, Checkbox, Container, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-core';
import { CardCategoryOption } from '../../../constants/BaseballCardConstants';
import { ManagerHubCheckResult, CoverageCheckResult } from '../../../hooks/useFolderChecks';
import { CoverageRow, indicatorType } from '../CoverageRow';
import completionEmailExample from '../../../assets/completion-email-example.png';
import './Step4Generate.scss';

export interface Step4GenerateProps {
  /** Number of people who will get a card — drives the success/warning copy. */
  selectedEmployeeCount: number;
  /** True while the start-generation request is in flight. */
  isSubmitting: boolean;
  /** Set once the backend accepts the run — drives the success state. */
  jobId: string | null;
  error: string | null;
  /** True when WorkDocs isn't ready (no token or folders) — surfaced as a warning here. */
  isWorkDocsReady: boolean;
  /** The selectable document-source categories rendered as checkboxes. */
  categoryOptions: readonly CardCategoryOption[];
  /** The currently-selected category values. */
  selectedCategories: Set<string>;
  /** Toggles one category value in/out of the selection. */
  onToggleCategory: (value: string) => void;
  /**
   * Advisory presence check for the Manager Hub Exports folder (one `manager-hub*.xlsx`). Null
   * until the folder checks have run; rendered as a small indicator beside its checkbox.
   */
  managerHubCheck: ManagerHubCheckResult | null;
  /**
   * Advisory per-employee coverage of the Supplemental Context subtree. Null until the checks have
   * run; rendered as a CoverageRow (summary + expandable missing-people list) beside its checkbox.
   */
  supplementalCheck: CoverageCheckResult | null;
}

/**
 * Step 4: review the generation summary and start the run. The trigger is the wizard's
 * submit button ("Generate Cards") in the footer, which fires an async backend job that
 * generates the cards into WorkDocs and emails the submitter on completion — so this step
 * confirms the run has started rather than downloading anything.
 */
export const Step4Generate: React.FC<Step4GenerateProps> = ({
  selectedEmployeeCount,
  isSubmitting,
  jobId,
  error,
  isWorkDocsReady,
  categoryOptions,
  selectedCategories,
  onToggleCategory,
  managerHubCheck,
  supplementalCheck
}) => (
  <Container>
    <SpaceBetween size="m">
      <SpaceBetween size="xs">
        {/* 16px heading via Cloudscape's heading-s token (no exact 16px Header variant exists). */}
        <Box variant="h3" fontSize="heading-s">
          Card Categories
        </Box>
        <Box variant="small" color="text-body-secondary">
          Choose which source materials to include in your cards. At least one is required.
        </Box>
        {categoryOptions.map((option) => (
          // Each category checkbox carries an optional advisory folder check beneath it: a small
          // presence indicator for Manager Hub Exports, or the per-employee coverage row for
          // Supplemental Context. The indicator is indented under the checkbox label and only
          // renders once its check has run (null until then).
          <SpaceBetween key={option.value} size="xxs">
            <Checkbox checked={selectedCategories.has(option.value)} onChange={() => onToggleCategory(option.value)} disabled={isSubmitting}>
              {option.label}
            </Checkbox>
            {option.value === 'manager_hub_exports' && managerHubCheck && (
              <Box padding={{ left: 'l' }}>
                <StatusIndicator type={indicatorType(managerHubCheck.status)}>{managerHubCheck.message}</StatusIndicator>
              </Box>
            )}
            {option.value === 'supplemental_docs' && supplementalCheck && (
              <Box padding={{ left: 'l' }}>
                <CoverageRow title="Supplemental Context" result={supplementalCheck} />
              </Box>
            )}
          </SpaceBetween>
        ))}
      </SpaceBetween>

      <Box variant="p" color="text-body-secondary">
        <Box variant="strong">What happens next:</Box> Cards are generated and saved directly to your WorkDocs folder. You&apos;ll receive an email
        when they&apos;re done — processing typically takes up to 15 minutes depending on team size and the amount of content uploaded. No need to
        stay on this screen; close it and return when your email arrives.
      </Box>

      <Box variant="p" color="text-body-secondary">
        <Box variant="strong">Confirmation email:</Box> Once generation is complete, you&apos;ll get an email. It confirms how many cards were created
        (e.g., &quot;6 of 6 created&quot;) and includes a direct link to open your cards folder in WorkDocs.
      </Box>

      <Box variant="p" color="text-body-secondary">
        <Box variant="strong">Reminder:</Box> These cards are AI-generated starting points. Always review and layer in your own context before using
        them in any decision.
      </Box>

      <img
        className="step4-generate__email-image"
        src={completionEmailExample}
        alt="Example completion email: subject 'Your baseball cards are ready — 6 of 6 created', with links to open your cards folder, go to Manager Hub, and open a support ticket"
      />

      {!isWorkDocsReady && <StatusIndicator type="warning">Connect WorkDocs and set up folders before generating.</StatusIndicator>}

      {selectedEmployeeCount === 0 && <StatusIndicator type="warning">Select at least one employee in Step 2.</StatusIndicator>}

      {selectedCategories.size === 0 && <StatusIndicator type="warning">Select at least one card category.</StatusIndicator>}

      {isSubmitting && <StatusIndicator type="in-progress">Starting generation…</StatusIndicator>}

      {jobId && (
        <Alert type="success" header="Generation started">
          {`Cards for ${selectedEmployeeCount} ${
            selectedEmployeeCount === 1 ? 'person' : 'people'
          } are on the way. We'll email you when they're ready (usually within 15 minutes) — feel free to close this screen.`}
        </Alert>
      )}

      {error && <StatusIndicator type="error">{error}</StatusIndicator>}
    </SpaceBetween>
  </Container>
);
